import { Box, Breadcrumbs, Typography } from '@mui/material'
import type { ReactElement } from 'react'
import { useContext, useMemo } from 'react'
import {
  IoCart,
  IoChatbubbles,
  IoHappy,
  IoHardwareChip,
  IoHelpCircleOutline,
  IoHomeSharp,
  IoLayers,
  IoNewspaper,
  IoNotifications,
  IoSettingsSharp,
} from 'react-icons/io5'
import { Link } from 'react-router-dom'
import { ClientContext } from '../providers/ClientProvider'
// import { IconType } from 'react-icons'

export type BreadId =
  | 'home'
  | 'device'
  | 'clients'
  | 'client'
  | 'notifications'
  | 'notification'
  | 'inquiries'
  | 'inquiry'
  | 'deviceOrders'
  | 'profile'
  | 'help'
  | 'news'
  | 'newsDetail'
  | 'subGroups'
  | 'subGroup'

type BreadItem = {
  id: BreadId
  text: string
  link?: string
  icon?: ReactElement
}

type Props = {
  breadIds: BreadId[]
}

export const breadItems = (clientId: string, isAdmin: boolean = false): BreadItem[] => [
  {
    id: 'home',
    link: `/clients/${clientId}/devices`,
    text: 'デバイス一覧',
    icon: <IoHomeSharp />,
  },
  {
    id: 'client',
    link: `/clients/${clientId}`,
    text: isAdmin ? 'グループ管理' : 'グループ詳細',
    icon: <IoHappy />,
  },
  {
    id: 'subGroups',
    link: `/clients/${clientId}/subGroups`,
    text: 'サブグループ',
    icon: <IoLayers />,
  },
  {
    id: 'subGroup',
    text: 'サブグループ詳細',
    icon: <IoLayers />,
  },
  {
    id: 'device',
    text: 'デバイス詳細',
    icon: <IoHardwareChip />,
  },
  {
    id: 'notifications',
    link: `/clients/${clientId}/notifications`,
    text: '通知一覧',
    icon: <IoNotifications />,
  },
  {
    id: 'notification',
    text: '通知詳細',
    icon: <IoNotifications />,
  },
  {
    id: 'deviceOrders',
    link: `/clients/${clientId}/orders`,
    text: 'デバイス注文',
    icon: <IoCart />,
  },
  {
    id: 'profile',
    text: 'プロフィール設定',
    icon: <IoSettingsSharp />,
  },
  {
    id: 'inquiries',
    link: `/clients/${clientId}/inquiries`,
    text: 'お問合せ一覧',
    icon: <IoChatbubbles />,
  },
  {
    id: 'inquiry',
    text: 'お問合せ詳細',
    icon: <IoChatbubbles />,
  },
  {
    id: 'news',
    link: `/news`,
    text: 'ニュース',
    icon: <IoNewspaper />,
  },
  {
    id: 'newsDetail',
    text: 'ニュース詳細',
    icon: <IoNewspaper />,
  },
  {
    id: 'clients',
    link: '/clients',
    text: 'グループ一覧',
    icon: <IoHappy />,
  },
  {
    id: 'help',
    link: `/help`,
    text: 'ヘルプ',
    icon: <IoHelpCircleOutline />,
  },
]

export const Breads = ({ breadIds }: Props) => {
  const { client, currentMember } = useContext(ClientContext)
  const isAdmin = currentMember?.role === 'admin'
  const breads = useMemo(() => {
    const items = breadItems(client?.id ?? '', isAdmin)
    return breadIds.map((id) => items.find((v) => v.id === id)).filter((v): v is BreadItem => !!v)
  }, [breadIds, client?.id, isAdmin])

  return (
    <Breadcrumbs separator="/" sx={{ mb: 3, mt: 1 }}>
      {breads.map((bread, i) => {
        if (!bread.link && !bread.icon) {
          return (
            <Typography
              key={bread.id}
              sx={{
                lineHeight: 1,
              }}
            >
              {bread.text}
            </Typography>
          )
        }
        if (!bread.link || i === breadIds.length - 1) {
          return (
            <Box
              key={bread.id}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                lineHeight: 1,
              }}
            >
              {bread.icon}
              {bread.text}
            </Box>
          )
        } else if (!bread.icon) {
          return (
            <Link key={bread.id} to={bread.link} className="breadLink">
              <Typography
                sx={{
                  lineHeight: 1,
                }}
              >
                {bread.text}
              </Typography>
            </Link>
          )
        }

        return (
          <Link key={bread.id} to={bread.link} className="breadLink">
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                lineHeight: 1,
              }}
            >
              {bread.icon}
              {bread.text}
            </Box>
          </Link>
        )
      })}
    </Breadcrumbs>
  )
}
